import { PARALLEL_DOT, clamp, lerp_float, vector3_pool } from '../../Math.js';
import { IVector3, IMatrix4, CameraMode, TransitionState, CameraTransition } from '../../api.js';
import { CameraManager } from './CameraManager.js';
import { ViewerCamera } from './ViewerCamera.js';
import { Vector3 as BABYLON_Vector3 } from '@inter-ikea-betrakta-babylonjs/core/Maths/math.vector.js';
/**
 * A class holding and updating the interpolated state between two cameras
 * It works by linear interpolation of position and target of both cameras
 * And performing a look at using the interpolated values at each step
 */
export class LerpPositionTargetTransitionCamera {
    constructor(_from_camera, _to_camera) {
        this._from_camera = _from_camera;
        this._to_camera = _to_camera;
        this._interpolation = 0;
        this._position = new IVector3();
        this._blend_target = new IVector3();
        this._forward = new IVector3();
        this._backward = new IVector3();
        this._left = new IVector3();
        this._right = new IVector3();
        this._up = new IVector3();
        this._down = new IVector3();
        this._view_matrix = new IMatrix4();
    }
    get blend_target() {
        return this._blend_target;
    }
    get position() {
        return this._position;
    }
    get forward() {
        return this._forward;
    }
    get backward() {
        return this._backward;
    }
    get left() {
        return this._left;
    }
    get right() {
        return this._right;
    }
    get up() {
        return this._up;
    }
    get down() {
        return this._down;
    }
    get view_matrix() {
        return this._view_matrix;
    }
    /**
     * Updates the state of this camera using the supplied interpolation value
     * @param t interpolation value
     */
    update(t) {
        this._interpolation = t;
        IVector3.lerp_to_ref(this._from_camera.position, this._to_camera.position, t, this._position);
        IVector3.lerp_to_ref(this._from_camera.blend_target, this._to_camera.blend_target, t, this._blend_target);
        const from_forward = this._from_camera.forward;
        const to_forward = this._to_camera.forward;
        const cos_omega = IVector3.dot(from_forward, to_forward);
        const forward = IVector3.subtract_to_ref(this.blend_target, this._position, vector3_pool.fetch()).normalize();
        const right = vector3_pool.fetch();
        const up = vector3_pool.fetch();
        if (Math.abs(cos_omega) < PARALLEL_DOT) {
            const sin_omega = Math.sqrt(1.0 - cos_omega * cos_omega);
            const omega = Math.acos(cos_omega);
            const slerp_t_times_omega = Math.acos(IVector3.dot(from_forward, forward));
            const scaled_source_right = vector3_pool.fetch();
            const scaled_target_right = vector3_pool.fetch();
            const to_right = vector3_pool.fetch().fromIVector3(this._to_camera.right);
            IVector3.add_to_ref(IVector3.scale_to_ref(this._from_camera.right, Math.sin(omega - slerp_t_times_omega) / sin_omega, scaled_source_right), IVector3.scale_to_ref(this._to_camera.right, Math.sin(slerp_t_times_omega) / sin_omega, scaled_target_right), right);
            // Since it's not guaranteed the slerping path of the right vector during the transition is always orthogonal to the forward vector, we remove the forward component of the right vector to make sure they're orthogonal.
            const forward_component = vector3_pool.fetch();
            right.subtractInPlace(forward.scaleToRef(forward.dot(right), forward_component));
            right.normalize();
            vector3_pool.return(scaled_source_right, scaled_target_right, forward_component, to_right);
        }
        else {
            if (Math.abs(forward.y) > PARALLEL_DOT) {
                up.copyFrom(ViewerCamera.FORWARD);
            }
            else {
                up.copyFrom(BABYLON_Vector3.UpReadOnly);
            }
            BABYLON_Vector3.CrossToRef(forward, up, right);
            right.normalize();
        }
        BABYLON_Vector3.CrossToRef(right, forward, up);
        IVector3.normalize_in_place(forward);
        IVector3.normalize_in_place(right);
        IVector3.normalize_in_place(up);
        IVector3.copy_to_ref(forward, this._forward);
        IVector3.copy_to_ref(right, this._right);
        IVector3.copy_to_ref(up, this._up);
        IVector3.scale_to_ref(forward, -1, this._backward);
        IVector3.scale_to_ref(right, -1, this._left);
        IVector3.scale_to_ref(up, -1, this._down);
        BABYLON_Vector3.CrossToRef(right, forward, up);
        this._view_matrix[0].x = right.x;
        this._view_matrix[0].y = right.y;
        this._view_matrix[0].z = right.z;
        this._view_matrix[0].w = 0;
        this._view_matrix[1].x = up.x;
        this._view_matrix[1].y = up.y;
        this._view_matrix[1].z = up.z;
        this._view_matrix[1].w = 0;
        this._view_matrix[2].x = forward.x;
        this._view_matrix[2].y = forward.y;
        this._view_matrix[2].z = forward.z;
        this._view_matrix[2].w = 0;
        this._view_matrix[3].x = this._position.x;
        this._view_matrix[3].y = this._position.y;
        this._view_matrix[3].z = this._position.z;
        this._view_matrix[3].w = 1;
        IMatrix4.inverse_in_place(this._view_matrix);
        vector3_pool.return(forward, up, right);
    }
    get_projection_matrix(_custom_aspect_ratio) {
        const from_projection = this._from_camera.get_projection_matrix(_custom_aspect_ratio);
        const to_projection = this._to_camera.get_projection_matrix(_custom_aspect_ratio);
        return IMatrix4.lerp(from_projection, to_projection, this._interpolation);
    }
}
/**
 * Viewers default implementation for a camera transition
 * This transition works by interpolation of target and position of two cameras using a supplied interpolation function that can be used to describe a curve if smoothing is wanted
 * It uses the LerpTargetPositionTransitionCamera to calculate the interpolated camera at each step
 */
export class LerpTargetPositionCameraTransition extends CameraTransition {
    constructor(_from_camera, _to_camera, engine_camera, duration, interpolation) {
        super(_from_camera, _to_camera, engine_camera);
        this.duration = duration;
        this.interpolation = interpolation;
        this._has_started = false;
        this._current_state = TransitionState.NONE;
        this._transition_camera = new LerpPositionTargetTransitionCamera(this.from_camera, this.to_camera);
        this.transition_generator = this.step_generator(_to_camera, this._transition_camera, engine_camera, duration, interpolation);
    }
    get has_started() {
        return this._current_state !== TransitionState.NONE;
    }
    get has_ended() {
        return this._has_started && (this._current_state === TransitionState.COMPLETED);
    }
    get transition_camera() {
        return this._transition_camera;
    }
    step(time, aspect_ratio) {
        this._current_state = this.transition_generator.next({ time: time, aspect_ratio: aspect_ratio }).value;
        if (this._current_state === TransitionState.STARTED) {
            this._has_started = true;
        }
        return this._current_state;
    }
    /**
     * Generator function used to handle the transition, stepped one step each time step is called.
     * @param to_camera Camera to transition to
     * @param transition_camera Camera used for calculating the interpolated state
     * @param engine_camera The camera that this transition should update on each step
     * @param duration the time this transition takes
     * @param interpolation given a value in range 0->1 outputs a value in range 0->1
     * @returns The current state of this transition, STARTED on the first call and then CONTINUE until it has finished and then it returns COMPLETED
     */
    *step_generator(to_camera, transition_camera, engine_camera, duration, interpolation) {
        let timer = 0;
        transition_camera.update(0);
        let update_data = yield TransitionState.STARTED;
        while (timer < duration) {
            timer += update_data.time.seconds;
            // Interpolate position and targets for calculating the view matrix
            const t = interpolation(clamp(timer / duration, 0, 1));
            transition_camera.update(t);
            this.fix_interpolated_projection(update_data.aspect_ratio);
            engine_camera.set_projection_matrix(this.fix_interpolated_projection(update_data.aspect_ratio), CameraMode.PERSPECTIVE);
            engine_camera.set_view_matrix(transition_camera.view_matrix);
            update_data = yield TransitionState.CONTINUE;
        }
        engine_camera.set_projection_matrix(to_camera.get_projection_matrix(), to_camera.projection.get_camera_mode());
        engine_camera.set_view_matrix(to_camera.view_matrix);
        return TransitionState.COMPLETED;
    }
}
// Here we monkey patch our CameraTransition function so that the implementation doesn't end up in api.ts
CameraTransition.prototype.fix_interpolated_projection = function (aspect_ratio) {
    const from_camera_frustum_world = CameraManager.get_frustum_vertices_world(this.from_camera);
    const to_camera_frustum_world = CameraManager.get_frustum_vertices_world(this.to_camera);
    const combined_frustum_world = from_camera_frustum_world.concat(to_camera_frustum_world);
    /**
     * Here we calculate the interpolated projection matrix in a few steps:
     *  1.  We simply get the interpolated projection matrix
     *  2.  Given the 2 cameras we're interpolating inbetween, calculating the extents of their frustums in world space
     *      (done earlier), and for our current interpolated view matrix, calculate which z near and far values we need,
     *      to not clip the scene no more than the original or new cameras do.
     *  3.  Calculate the values in a projection related to z near/far values, for both a perspective and orthographic projection matrix,
     *      using the calculated near/far.
     *      Then, we utilize that in any perspective projection matrix, we have an element that is always equal to 1 or -1,
     *      while it is always 0 in an orthographic projection.
     *      So when interpolating the projection matrix, we can take the absolute value of this element to measure how
     *      "orthographic vs perspective" we currently are in the interpolation, and then simply mix our z-related values
     *      with this interpolant, to get good near/far values in our interpolated projection matrix.
    */
    const current_proj_matrix = this.transition_camera.get_projection_matrix(aspect_ratio);
    const current_view_matrix = this.transition_camera.view_matrix;
    // Arbitrary value to clamp to for perspective near
    const minimum_perspective_near = 0.01;
    let perspective_near = Number.MAX_VALUE;
    let perspective_far = -Number.MAX_VALUE;
    let orthographic_near = Number.MAX_VALUE;
    let orthographic_far = -Number.MAX_VALUE;
    // Calculate the required near/far values for our 2 camera's projections
    const projected = vector3_pool.fetch();
    for (let i = 0; i < combined_frustum_world.length; i++) {
        IMatrix4.transform_vector3_to_ref(current_view_matrix, combined_frustum_world[i], projected);
        perspective_near = Math.min(perspective_near, Math.max(minimum_perspective_near, projected.z));
        perspective_far = Math.max(perspective_far, projected.z);
        orthographic_near = Math.min(orthographic_near, projected.z);
        orthographic_far = Math.max(orthographic_far, projected.z);
    }
    vector3_pool.return(projected);
    // Our measure of how "perspective" our current matrix is
    const perspectiveness = Math.abs(current_proj_matrix[2].w);
    // The near/far related values for a perspective projection
    const perspective_c = (perspective_far + perspective_near) / (perspective_far - perspective_near);
    const perspective_d = -2.0 * perspective_far * perspective_near / (perspective_far - perspective_near);
    // The near/far related values for an orthographic projection
    const orthographic_c = 2.0 / (orthographic_far - orthographic_near);
    const orthographic_d = -(orthographic_near + orthographic_far) / (orthographic_far - orthographic_near);
    // Mix between orthographic and perspective in our interpolated matrix
    current_proj_matrix[2].z = lerp_float(orthographic_c, perspective_c, perspectiveness);
    current_proj_matrix[3].z = lerp_float(orthographic_d, perspective_d, perspectiveness);
    return current_proj_matrix;
};
//# sourceMappingURL=CameraTransition.js.map